import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  TrophyIcon,
  FireIcon,
  HeartIcon,
  ChatBubbleLeftIcon,
  BookOpenIcon,
  SparklesIcon,
  StarIcon,
  BoltIcon,
  GlobeAltIcon,
  UserGroupIcon,
  CheckBadgeIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline'

const AchievementsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'all' | 'unlocked' | 'locked'>('all')

  const achievements: Achievement[] = [
    {
      id: 1,
      title: 'First Connection',
      description: 'Made your first match',
      icon: HeartIcon,
      unlocked: true,
      progress: 100,
      points: 10,
      rarity: 'common'
    },
    {
      id: 2,
      title: 'Conversation Starter',
      description: 'Send 10 messages',
      icon: ChatBubbleLeftIcon,
      unlocked: true,
      progress: 100,
      points: 20,
      rarity: 'common'
    },
    {
      id: 3,
      title: 'Storyteller',
      description: 'Share your first story',
      icon: BookOpenIcon,
      unlocked: true,
      progress: 100,
      points: 25,
      rarity: 'uncommon'
    },
    {
      id: 4,
      title: 'Popular Creator',
      description: 'Get 100 likes on a story',
      icon: StarIcon,
      unlocked: true,
      progress: 100,
      points: 50,
      rarity: 'rare'
    },
    {
      id: 5,
      title: 'Speed Demon',
      description: 'Make 5 matches in a day',
      icon: BoltIcon,
      unlocked: false,
      progress: 60,
      points: 30,
      rarity: 'uncommon'
    },
    {
      id: 6,
      title: 'Social Butterfly',
      description: 'Have 50 active conversations',
      icon: UserGroupIcon,
      unlocked: false,
      progress: 34,
      points: 75,
      rarity: 'rare'
    },
    {
      id: 7,
      title: 'Global Citizen',
      description: 'Match with people from 10 countries',
      icon: GlobeAltIcon,
      unlocked: false,
      progress: 40,
      points: 100,
      rarity: 'epic'
    },
    {
      id: 8,
      title: 'Legendary Lover',
      description: 'Maintain a 30-day streak',
      icon: FireIcon,
      unlocked: false,
      progress: 23,
      points: 200,
      rarity: 'legendary'
    },
    {
      id: 9,
      title: 'Premium Elite',
      description: 'Subscribe to Premium for 6 months',
      icon: SparklesIcon,
      unlocked: false,
      progress: 16,
      points: 150,
      rarity: 'epic'
    },
    {
      id: 10,
      title: 'Community Legend',
      description: 'Reach 1000 total matches',
      icon: TrophyIcon,
      unlocked: false,
      progress: 12,
      points: 500,
      rarity: 'legendary'
    }
  ]

  const filteredAchievements = achievements.filter(achievement => {
    if (activeTab === 'unlocked') return achievement.unlocked
    if (activeTab === 'locked') return !achievement.unlocked
    return true
  })

  const totalPoints = achievements
    .filter(a => a.unlocked)
    .reduce((sum, a) => sum + a.points, 0)

  const unlockedCount = achievements.filter(a => a.unlocked).length

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-primary-600 to-secondary-600 mb-4"
        >
          <TrophyIcon className="h-10 w-10 text-white" />
        </motion.div>
        
        <h1 className="text-4xl md:text-5xl font-display font-bold mb-4">
          Your <span className="gradient-text">Achievements</span>
        </h1>
        <p className="text-white/70 text-lg">
          Track your progress and unlock exclusive rewards
        </p>
      </motion.div>

      {/* Stats Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        <div className="card-glass p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-white/70">Achievements</span>
            <CheckBadgeIcon className="h-8 w-8 text-primary-400" />
          </div>
          <p className="text-3xl font-bold">
            {unlockedCount} / {achievements.length}
          </p>
          <div className="mt-4 bg-white/10 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-primary-600 to-secondary-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${(unlockedCount / achievements.length) * 100}%` }}
            />
          </div>
        </div>

        <div className="card-glass p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-white/70">Total Points</span>
            <StarIcon className="h-8 w-8 text-accent-400" />
          </div>
          <p className="text-3xl font-bold text-accent-400">{totalPoints}</p>
          <p className="text-sm text-white/50 mt-2">Keep unlocking to earn more!</p>
        </div>

        <div className="card-glass p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-white/70">Current Streak</span>
            <FireIcon className="h-8 w-8 text-orange-400" />
          </div>
          <p className="text-3xl font-bold text-orange-400">7 Days</p>
          <p className="text-sm text-white/50 mt-2">Your best: 12 days</p>
        </div>
      </motion.div>

      {/* Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="flex space-x-2 border-b border-white/10 pb-2"
      >
        {(['all', 'unlocked', 'locked'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-3 rounded-t-xl font-medium transition-all capitalize ${
              activeTab === tab
                ? 'bg-gradient-to-r from-primary-600/20 to-secondary-600/20 text-white border-b-2 border-primary-500'
                : 'text-white/70 hover:text-white hover:bg-white/5'
            }`}
          >
            {tab}
          </button>
        ))}
      </motion.div>

      {/* Achievements Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {filteredAchievements.map((achievement, index) => (
          <AchievementCard
            key={achievement.id}
            achievement={achievement}
            index={index}
          />
        ))}
      </motion.div>
    </div>
  )
}

interface Achievement {
  id: number
  title: string
  description: string
  icon: React.ComponentType<any>
  unlocked: boolean
  progress: number
  points: number
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary'
}

interface AchievementCardProps {
  achievement: Achievement
  index: number
}

const AchievementCard: React.FC<AchievementCardProps> = ({ achievement, index }) => {
  const rarityColors = {
    common: 'from-gray-400 to-gray-600',
    uncommon: 'from-green-400 to-green-600',
    rare: 'from-blue-400 to-blue-600',
    epic: 'from-purple-400 to-purple-600',
    legendary: 'from-orange-400 to-orange-600'
  }

  const Icon = achievement.icon

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.05 }}
      whileHover={{ scale: 1.03, y: -5 }}
      className={`card-glass p-6 relative overflow-hidden ${
        !achievement.unlocked ? 'opacity-60' : ''
      }`}
    >
      {/* Rarity Badge */}
      <div className={`absolute top-4 right-4 px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r ${rarityColors[achievement.rarity]} text-white`}>
        {achievement.rarity.toUpperCase()}
      </div>

      {/* Icon */}
      <div className={`relative inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
        achievement.unlocked
          ? 'bg-gradient-to-r from-primary-600 to-secondary-600'
          : 'bg-white/10'
      }`}>
        {achievement.unlocked ? (
          <Icon className="h-8 w-8 text-white" />
        ) : (
          <LockClosedIcon className="h-8 w-8 text-white/50" />
        )}
      </div>

      {/* Title & Description */}
      <h3 className="text-xl font-bold mb-2">{achievement.title}</h3>
      <p className="text-white/70 text-sm mb-4">{achievement.description}</p>

      {/* Progress Bar (for locked achievements) */}
      {!achievement.unlocked && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-white/50">Progress</span>
            <span className="text-white/70">{achievement.progress}%</span>
          </div>
          <div className="bg-white/10 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-primary-600 to-secondary-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${achievement.progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Points */}
      <div className="flex items-center justify-between pt-4 border-t border-white/10">
        <span className="text-white/50 text-sm">Reward</span>
        <div className="flex items-center space-x-2">
          <StarIcon className="h-5 w-5 text-accent-400" />
          <span className="font-bold text-accent-400">{achievement.points} pts</span>
        </div>
      </div>

      {achievement.unlocked && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute bottom-4 right-4"
        >
          <div className="bg-green-500 rounded-full p-2">
            <CheckBadgeIcon className="h-5 w-5 text-white" />
          </div>
        </motion.div>
      )}
    </motion.div>
  )
}

export default AchievementsPage
